/// <reference path="../_references.ts"/>

module App.Appointments {
	interface LocalStorage extends ng.local.storage.ILocalStorageService {
		selectedAppointmentId: number;
	}

	export class SelectedAppointment implements Appointment {
		public NO_APPOINTMENT: string  = 'Warning: No appointment set.';

		[key: string]: any;

		id: number;
		appointment_status: string;
		service_request: ServiceRequest;
		scheduled_date: string;
		scheduled_time: string;
		scheduled_duration: number;
		estimated_completion_time: string;
		service_plan: ServicePlan;
		is_partial?: boolean = true;

		private isDataSet = false;
		private loading?: number = null;
		private loadCallbacks: Array<(appointment: Appointment) => any> = [];
		private callbacks: Array<(appointment: Appointment) => any> = [];

		constructor(
			private $localStorage: LocalStorage,
			private AppointmentService: AppointmentService,
			private $q: ng.IQService
		) {

		}

		private noAppointment(): ng.IPromise<any> {
			let rejection = this.$q.defer<any>();
			rejection.reject(this.NO_APPOINTMENT);
			return rejection.promise;
		}

		getAppointment(): ng.IPromise<Appointment> {
			if (this.isSet() && !this.is_partial) {
				return this.$q.when(this);
			}

			return this.loadFromStorage();
		}

		set(appointment: Appointment, full?: boolean): void {
			this.$localStorage.selectedAppointmentId = appointment.id;

			this.id = appointment.id;
			this.appointment_status = appointment.appointment_status;
			this.service_request = appointment.service_request;
			this.scheduled_date = appointment.scheduled_date;
			this.scheduled_time = appointment.scheduled_time;
			this.scheduled_duration = appointment.scheduled_duration;
			this.estimated_completion_time = appointment.estimated_completion_time;
			this.is_partial = !full;

			this.isDataSet = true;

			this.callbacks.forEach((callback) => {
				callback.call(null, this);
			});
		}

		setOnly(keys: string[], appointment: Appointment): void {
			if (this.id !== appointment.id) {
				this.clear();
			}

			this.id = appointment.id;
			this.isDataSet = true;
			keys.forEach((key) => this[key] = appointment[key]);
		}

		clear(): void {
			this.id = null;
			this.appointment_status = null;
			this.service_request = null;
			this.scheduled_date = null;
			this.scheduled_time = null;
			this.estimated_completion_time = null;
			this.scheduled_duration = null;

			this.isDataSet = false;
			this.is_partial = true;

			this.$localStorage.selectedAppointmentId = null;

			this.callbacks.forEach((callback) => {
				callback.call(null, this);
			});
		}

		isSet(): boolean {
			return this.isDataSet;
		}

		loadFromStorage(): ng.IPromise<Appointment> {
			const vm = this;

			if (vm.$localStorage.selectedAppointmentId !== null && angular.isDefined(vm.$localStorage.selectedAppointmentId)) {
				const id = vm.$localStorage.selectedAppointmentId;
				if (vm.loading === id) {
					var defer = this.$q.defer();
					this.loadCallbacks.push(defer.resolve.bind(defer));
					return <ng.IPromise<Appointment>>defer.promise;
				}

				vm.loading = id;

				return vm.AppointmentService.fetch(id, {
					show_tags: 1,
					show_files: 1,
				}).then((response) => {
					vm.set(response.data, true);
					vm.loading = null;

					this.loadCallbacks.forEach((callback) => {
						callback.call(null, this);
					});
					this.loadCallbacks = [];

					return this;
				});
			}

			return this.noAppointment();
		}

		updateAppointmentEtc(etc: string, duration: number, skipUpdate?: boolean): ng.IPromise<Appointment> {
			if (!this.id) {
				return this.noAppointment();
			}

			return this.AppointmentService.updateEtc(this.id, etc, duration).then((response) => {
				if(!skipUpdate) {
					this.setOnly(['estimated_completion_time', 'scheduled_duration'], response.data);
				}
				return this;
			});
		}

		updateAppointmentStatus(status: string, skipUpdate?: boolean): ng.IPromise<Appointment> {
			if (!this.id) {
				return this.noAppointment();
			}

			return this.AppointmentService.updateStatus(this.id, status).then((response) => {
				if(!skipUpdate) {
					this.setOnly(['appointment_status'], response.data);
				}
				return this;
			});
		}

		sendCustomerText(status: string, additionalText?: string) {
			this.AppointmentService.sendCustomerText(this.id, status, additionalText);
		}

		pauseAppointment(): ng.IHttpPromise<void> {
			return this.AppointmentService.pauseAppointment(this.id);
		}

		getLocation() {
			const vm = this,
				sr = vm.service_request;

			let location: Location,
				locationName = '';

			if(sr) {
				if(sr.location) {
					location = sr.location;
				} else if(sr.service_address && sr.service_address.location) {
					location = sr.service_address.location;
				}
			}

			if(location) {
				locationName = location.name;
			}

			return locationName;
		}

		getHours() {
			const vm = this,
				sr = vm.service_request;
			let hours = 'Emergency Service';

			if(sr) {
				if(sr.customer && sr.customer.rate_mode) {
					if(sr.customer.rate_mode.regular) {
						hours = 'Business Hours';
					}
				}
			}

			return hours;
		}

		getEmail() {
			const sr = this.service_request;

			let email: Email,
				emailAddress = 'No email found';

			if(sr) {
				if(sr.customer && sr.customer.emails) {
					email = sr.customer.emails[0];
					if(email) {
						emailAddress = email.email;
					}
				}
			}

			return emailAddress;
		}

		getCustomerName(): string {
			const customer = this.service_request && this.service_request.customer;
			if (!customer) {
				return '';
			}

			const hasName = Boolean(customer.first_name + customer.last_name);
			let name = customer.company_name ? (customer.company_name + (hasName ? ': ' : '')) : '';
			name += customer.first_name ? (customer.first_name + (customer.last_name ? ' ' : '')) : '';
			name += customer.last_name ? customer.last_name : '';

			return name;
		}

		getServiceAddress(): string {
			const sr = this.service_request;

			return sr && sr.service_address
				? sr.service_address.address
				: '';
		}

		getFirstPhone(mobileOnly?: boolean): PhoneNumber {
			const sr = this.service_request;

			if (sr && sr.customer && sr.customer.phone_numbers && sr.customer.phone_numbers.length) {
				if (mobileOnly) {
					return sr.customer.phone_numbers.find(number => {
						return number.send_text === true;
					});
				} else {
					return sr.customer.phone_numbers[0];
				}
			}

			return null;
		}

		getPhone(format?: boolean, mobileOnly?: boolean) {
			const phone: PhoneNumber = this.getFirstPhone(mobileOnly);

			return phone
				? format
					? this.formatPhone(phone.number)
					: phone.number
				: 'No phone found';
		};

		formatPhone(number: string) {
			return number && number.length > 5
				? number.replace(/(\d\d\d)(\d\d\d)(\d\d\d\d)/, '($1) $2-$3')
				: number;
		}

		addListener(callback: (appointment: Appointment) => any) {
			this.callbacks.push(callback);
		}

		removeListener(callback: () => any) {
			const index = this.callbacks.indexOf(callback);
			if (index >= 0) {
				this.callbacks.splice(index, 1);
			}
		}


		public static Factory() {
			let instance = ($localStorage: LocalStorage, AppointmentService: AppointmentService, $q: ng.IQService) => {
				return new SelectedAppointment($localStorage, AppointmentService, $q);
			};

			instance.$inject = ['$localStorage', 'AppointmentService', '$q'];

			return instance;
		}
	}
}